import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
    FileSearch, 
    Calendar, 
    User, 
    Car, 
    Mail, 
    CheckCircle, 
    XCircle, 
    Clock, 
    Search, 
    ChevronRight, 
    Eye, 
    ExternalLink,
    Shield,
    FileText,
    Brain,
    MessageSquare,
    AlertTriangle,
    AlertCircle,
    ArrowRight,
    RotateCcw,
    RotateCw,
    PlayCircle,
    Send,
    RefreshCw
} from 'lucide-react';

interface AuditFile {
    id: string;
    file_url: string;
    text_content: string;
}

interface Audit {
    id: string;
    email: string;
    car_model: string;
    description: string;
    status: string;
    result_json: any;
    email_sequence_status: number;
    sent_email_html: string;
    created_at: string;
    updated_at: string;
    audit_files: AuditFile[];
}

export function AdminAudits() {
    const [audits, setAudits] = useState<Audit[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);

    const fetchAudits = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('audit')
                .select('*, audit_files(*)')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAudits(data || []);
        } catch (err) {
            console.error('Failed to fetch audits:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRestart = async (auditId: string) => {
        try {
            // 1. Reset status in DB
            const { error: resetError } = await supabase
                .from('audit')
                .update({ status: 'pending', result_json: null })
                .eq('id', auditId);
            
            if (resetError) throw resetError;

            // 2. Trigger worker
            await fetch('https://mycarcosts.gtreulands.workers.dev/audit/trigger', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ auditId })
            });

            // 3. Refresh list
            fetchAudits();
            if (selectedAudit?.id === auditId) {
                const updated = await supabase.from('audit').select('*, audit_files(*)').eq('id', auditId).single();
                setSelectedAudit(updated.data);
            }
        } catch (err) {
            console.error('Restart failed:', err);
            alert('Restart failed. Check console.');
        }
    };

    const handleReset = async (auditId: string) => {
        if (!window.confirm('WARNING: This will DELETE all AI results and reset this audit to pending. Proceed?')) return;
        try {
            const { error } = await supabase
                .from('audit')
                .update({ 
                    status: 'pending', 
                    result_json: null, 
                    email_sequence_status: null 
                })
                .eq('id', auditId);
            
            if (error) throw error;
            toast.success('Audit fully reset');
            fetchAudits();
            if (selectedAudit?.id === auditId) setSelectedAudit(null);
        } catch (err: any) {
            toast.error(err.message || 'Reset failed');
        }
    };

    const handleNextStep = async (auditId: string) => {
        try {
            const res = await fetch('https://mycarcosts.gtreulands.workers.dev/audit/next-step', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ auditId })
            });
            if (!res.ok) throw new Error(await res.text());
            
            fetchAudits();
            if (selectedAudit?.id === auditId) {
                const updated = await supabase.from('audit').select('*, audit_files(*)').eq('id', auditId).single();
                setSelectedAudit(updated.data);
            }
        } catch (err) {
            console.error('Next step failed:', err);
            alert('Next step failed.');
        }
    };

    const handleRunAll = async () => {
        try {
            await fetch('https://mycarcosts.gtreulands.workers.dev/audit/run-all', { method: 'POST' });
            alert('Background processing triggered for all pending audits!');
            fetchAudits();
        } catch (err) {
            console.error('Run all failed:', err);
        }
    };

    useEffect(() => {
        fetchAudits();
    }, []);

    const filteredAudits = audits.filter(a => 
        a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.car_model?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
            processing: 'bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse',
            completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
            failed: 'bg-red-500/10 text-red-500 border-red-500/20'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${styles[status] || styles.pending}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in font-primary">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-white flex items-center gap-3">
                        <FileSearch size={32} className="text-blue-500" />
                        Mechanic Quote Audits
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium">Manage lead magnet submissions and AI processing</p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={fetchAudits}
                        className="p-3 bg-slate-900 border border-slate-800 text-slate-400 hover:text-white rounded-2xl transition-all"
                        title="Refresh List"
                    >
                        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button 
                        onClick={handleRunAll}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-600/20"
                    >
                        <PlayCircle size={18} />
                        Trigger Background
                    </button>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email or car..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-900 border border-slate-800 rounded-2xl py-3 pl-12 pr-6 w-full md:w-80 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold text-white placeholder:text-slate-600"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-[2rem] overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-950/50 border-b border-slate-800">
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Submission</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Car Model</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Sequence</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Files</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={6} className="px-6 py-8 h-20 bg-slate-900/50"></td>
                                    </tr>
                                ))
                            ) : filteredAudits.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500 font-bold">
                                        No audits found matching your criteria.
                                    </td>
                                </tr>
                            ) : (
                                filteredAudits.map((audit) => (
                                    <tr 
                                        key={audit.id} 
                                        onClick={() => setSelectedAudit(audit)}
                                        className="hover:bg-slate-800/80 transition-all group cursor-pointer border-b border-slate-800/50"
                                    >
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-white group-hover:text-blue-400 transition-colors">{audit.email}</span>
                                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                                    <Clock size={12} />
                                                    {new Date(audit.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2 font-black text-slate-300">
                                                <Car size={16} className="text-slate-500" />
                                                {audit.car_model || 'Unknown'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            {getStatusBadge(audit.status)}
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-1.5">
                                                <div className="flex gap-0.5">
                                                    {[1, 2, 3, 4].map(step => (
                                                        <div 
                                                            key={step} 
                                                            className={`w-2 h-2 rounded-full ${audit.email_sequence_status >= step ? 'bg-blue-500' : 'bg-slate-700'}`} 
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">
                                                    Step {audit.email_sequence_status}/4
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="px-2 py-1 rounded-lg bg-slate-800 text-xs font-bold text-slate-400">
                                                {audit.audit_files?.length || 0}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Modal */}
            {selectedAudit && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 animate-fade-in">
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setSelectedAudit(null)}></div>
                    <div className="relative bg-slate-900 border border-slate-800 w-full max-w-6xl max-h-[90vh] rounded-[3rem] overflow-hidden flex flex-col shadow-2xl">
                        {/* Modal Header */}
                        <div className="p-8 border-b border-slate-800 flex items-center justify-between shrink-0 bg-slate-950/50">
                            <div className="flex items-center gap-6">
                                <div className="p-4 bg-blue-600/20 rounded-2xl border border-blue-600/30 text-blue-500">
                                    <FileSearch size={32} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h2 className="text-2xl font-black text-white">{selectedAudit.email}</h2>
                                        {getStatusBadge(selectedAudit.status)}
                                    </div>
                                    <p className="text-slate-500 font-bold flex items-center gap-2 uppercase tracking-widest text-[10px]">
                                        <Car size={14} />
                                        {selectedAudit.car_model || 'Unknown model'}
                                        <span className="opacity-20 mx-1">|</span>
                                        <Clock size={14} />
                                        {new Date(selectedAudit.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => handleReset(selectedAudit.id)}
                                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all border border-slate-700"
                                >
                                    <RotateCw size={16} />
                                    Reset State
                                </button>
                                {selectedAudit.status !== 'completed' && selectedAudit.status !== 'finalizing' && (
                                    <button 
                                        onClick={() => handleRestart(selectedAudit.id)}
                                        className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all"
                                    >
                                        <RotateCcw size={16} />
                                        Force Restart
                                    </button>
                                )}
                                {selectedAudit.status === 'completed' && selectedAudit.email_sequence_status < 4 && (
                                    <button 
                                        onClick={() => handleNextStep(selectedAudit.id)}
                                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all"
                                    >
                                        <Send size={16} />
                                        Next Email
                                    </button>
                                )}
                                <button 
                                    onClick={() => setSelectedAudit(null)}
                                    className="p-3 bg-slate-800 hover:bg-red-600 hover:text-white rounded-2xl transition-all text-slate-400"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Description Block */}
                        {selectedAudit.description && (
                            <div className="px-8 pt-8 bg-slate-900">
                                <div className="p-6 bg-slate-950 rounded-3xl border border-blue-900/20">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-500 mb-2 flex items-center gap-2">
                                        <MessageSquare size={14} />
                                        User Problem Description
                                    </h4>
                                    <p className="text-slate-300 font-medium leading-relaxed">
                                        {selectedAudit.description}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-8 space-y-12">
                            {/* Grid Layout: Files & Vision on left, AI Pipeline on right */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                {/* Left Column: Files and Extracted Text */}
                                <div className="space-y-8">
                                    <h3 className="text-xl font-black text-white flex items-center gap-2">
                                        <FileText size={20} className="text-blue-500" />
                                        Source Documents
                                    </h3>

                                    {/* Error Display if Failed */}
                                    {selectedAudit.status === 'failed' && (
                                        <div className="p-8 bg-red-600/10 border-2 border-red-600/20 rounded-[2.5rem] animate-pulse-slow">
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className="p-3 bg-red-600 rounded-2xl text-white">
                                                    <AlertCircle size={24} />
                                                </div>
                                                <div>
                                                    <h4 className="text-red-500 font-black uppercase tracking-widest text-[10px]">Processing Error</h4>
                                                    <p className="text-white font-bold">{selectedAudit.result_json?.error || 'Unknown fatal error'}</p>
                                                </div>
                                            </div>
                                            <div className="bg-slate-950/80 p-5 rounded-2xl text-xs font-mono text-red-400 border border-red-900/30">
                                                {JSON.stringify(selectedAudit.result_json, null, 2)}
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="space-y-6">
                                        {selectedAudit.audit_files?.map((file, idx) => (
                                            <div key={file.id} className="bg-slate-950/50 rounded-3xl border border-slate-800 overflow-hidden">
                                                <div className="p-4 bg-slate-950 border-b border-slate-800 flex items-center justify-between">
                                                    <span className="text-xs font-black uppercase tracking-widest text-slate-500">Document #{idx + 1}</span>
                                                    <a href={file.file_url} target="_blank" className="text-xs font-bold text-blue-500 hover:text-blue-400 flex items-center gap-1">
                                                        <ExternalLink size={14} />
                                                        Open Full
                                                    </a>
                                                </div>
                                                <div className="p-6">
                                                    {file.file_url.endsWith('.pdf') ? (
                                                        <div className="aspect-video bg-slate-900 rounded-xl flex items-center justify-center border border-slate-800">
                                                            <FileText size={48} className="text-slate-700" />
                                                        </div>
                                                    ) : (
                                                        <img src={file.file_url} alt="Quote" className="w-full rounded-xl border border-slate-800" />
                                                    )}
                                                    
                                                    <div className="mt-6 space-y-2">
                                                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                                            <Brain size={14} />
                                                            Vision Extraction (Debug)
                                                        </div>
                                                        <div className="bg-slate-950 p-4 rounded-xl text-sm font-medium text-slate-400 font-mono whitespace-pre-wrap max-h-40 overflow-y-auto border border-slate-800">
                                                            {file.text_content || 'No text extracted yet.'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Column: AI Insights & Sequence */}
                                <div className="space-y-8">
                                    <h3 className="text-xl font-black text-white flex items-center gap-2">
                                        <Brain size={20} className="text-purple-500" />
                                        AI Pipeline Analysis
                                    </h3>

                                    {!selectedAudit.result_json ? (
                                        <div className="p-12 text-center bg-slate-950/50 rounded-[2.5rem] border border-dashed border-slate-800">
                                            <CpuIcon className="w-16 h-16 text-slate-800 mx-auto mb-4 animate-pulse" />
                                            <p className="text-slate-500 font-bold">Waiting for processing to complete...</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-8">
                                            {/* Analyst Step */}
                                            <div className="p-8 bg-blue-600/5 border border-blue-600/20 rounded-[2.5rem]">
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="px-3 py-1 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full">Step 1: Junior Analyst</div>
                                                    <div className="text-blue-500 font-black italic">{selectedAudit.result_json.analyst?.verdict}</div>
                                                </div>
                                                <div className="bg-slate-950/80 p-5 rounded-2xl text-xs font-mono text-slate-400 max-h-60 overflow-y-auto border border-blue-900/30">
                                                    {JSON.stringify(selectedAudit.result_json.analyst, null, 2)}
                                                </div>
                                            </div>

                                            {/* Auditor Step */}
                                            <div className="p-8 bg-purple-600/5 border border-purple-600/20 rounded-[2.5rem] relative overflow-hidden">
                                                <div className="absolute top-0 right-0 p-8 opacity-5">
                                                    <Shield size={120} className="text-purple-500" />
                                                </div>
                                                <div className="relative z-10">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div className="px-3 py-1 bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full">Step 2: Senior Auditor</div>
                                                        <div className="text-purple-500 font-black italic text-xl uppercase">{selectedAudit.result_json.auditor?.verdict}</div>
                                                    </div>
                                                    <div className="p-4 bg-slate-900 rounded-2xl border border-purple-900/20 mb-4">
                                                        <div className="text-[10px] font-black uppercase tracking-widest text-purple-500 mb-1">Decision Punchline</div>
                                                        <div className="text-white font-bold">{selectedAudit.result_json.auditor?.decision_punchline}</div>
                                                    </div>
                                                    {selectedAudit.result_json.auditor?.future_roadmap_teaser && (
                                                        <div className="p-4 bg-indigo-900/20 rounded-2xl border border-indigo-900/20 mb-4">
                                                            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-1">Roadmap Teaser</div>
                                                            <div className="text-indigo-100 font-bold italic">"{selectedAudit.result_json.auditor.future_roadmap_teaser}"</div>
                                                        </div>
                                                    )}
                                                    <div className="bg-slate-950/80 p-5 rounded-2xl text-xs font-mono text-slate-300 max-h-60 overflow-y-auto border border-purple-900/30">
                                                        {JSON.stringify(selectedAudit.result_json.auditor, null, 2)}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Email Sequence Preview */}
                                            <div className="p-8 bg-slate-950/50 border border-slate-800 rounded-[2.5rem]">
                                                <div className="flex items-center justify-between mb-6">
                                                    <h4 className="text-lg font-black text-white flex items-center gap-2">
                                                        <Mail size={18} className="text-emerald-500" />
                                                        Email Nurture Sequence
                                                    </h4>
                                                    {selectedAudit.sent_email_html && (
                                                        <button 
                                                            onClick={() => {
                                                                const win = window.open('', '_blank');
                                                                win?.document.write(selectedAudit.sent_email_html);
                                                            }}
                                                            className="text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-400 flex items-center gap-1.5"
                                                        >
                                                            <Eye size={14} />
                                                            View Sent HTML
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="space-y-4 text-sm">
                                                    <div className="flex items-center justify-between p-4 bg-slate-900 rounded-2xl border border-slate-800">
                                                        <div className="flex items-center gap-3 text-emerald-500 font-black">
                                                            <CheckCircle size={18} />
                                                            Email #1: Results
                                                        </div>
                                                        <span className="text-[10px] font-black text-slate-500 uppercase">Sent immediately</span>
                                                    </div>
                                                    <div className={`p-4 rounded-2xl border flex items-center justify-between ${selectedAudit.email_sequence_status >= 2 ? 'bg-slate-900 border-slate-800 text-emerald-500' : 'bg-slate-950 border-dashed border-slate-800 text-slate-600'}`}>
                                                        <div className="flex items-center gap-3 font-black">
                                                            <Clock size={18} />
                                                            Email #2: Follow-up
                                                        </div>
                                                        <span className="text-[10px] font-black uppercase tracking-widest">~24H LATER</span>
                                                    </div>
                                                    <div className={`p-4 rounded-2xl border flex items-center justify-between ${selectedAudit.email_sequence_status >= 3 ? 'bg-slate-900 border-slate-800 text-emerald-500' : 'bg-slate-950 border-dashed border-slate-800 text-slate-600'}`}>
                                                        <div className="flex items-center gap-3 font-black">
                                                            <Clock size={18} />
                                                            Email #3: Offer
                                                        </div>
                                                        <span className="text-[10px] font-black uppercase tracking-widest">~72H LATER</span>
                                                    </div>
                                                </div>
                                                
                                                {selectedAudit.sent_email_html && (
                                                    <div className="mt-8">
                                                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                                                            <Shield size={14} />
                                                            Email Payload Preview
                                                        </div>
                                                        <div className="w-full h-[500px] bg-white rounded-2xl overflow-hidden border border-slate-800">
                                                            <iframe 
                                                                srcDoc={selectedAudit.sent_email_html} 
                                                                className="w-full h-full border-none"
                                                                title="Email Preview"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CpuIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect width="16" height="16" x="4" y="4" rx="2" />
            <rect width="6" height="6" x="9" y="9" rx="1" />
            <path d="M15 2v2" />
            <path d="M15 20v2" />
            <path d="M2 15h2" />
            <path d="M2 9h2" />
            <path d="M20 15h2" />
            <path d="M20 9h2" />
            <path d="M9 2v2" />
            <path d="M9 20v2" />
        </svg>
    )
}
